import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as productStore from '@cartwhl/common/stores/productStore';
import { isPro, isUnlimited } from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/Text';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

type PurchaseSpinsModalProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      close?: never;
    }
  | {
      close: () => void;
      open?: never;
      onOpenChange?: never;
    };

export default function PurchaseSpinsModal(props: PurchaseSpinsModalProps) {
  const open = 'open' in props ? props.open : true;
  const { products } = useSnapshot(productStore.state);
  const [quantity, setQuantity] = useState(1);

  const priceObject = productStore.getPriceByID(
    products?.more?.default_price ?? '',
  );
  const spinPrice = priceObject?.unit_amount ?? 500;
  const isPayingCustomer = isPro() || isUnlimited();

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  function handleOpenChange(next: boolean) {
    if (typeof props.onOpenChange === 'function') {
      props.onOpenChange(next);
      return;
    }
    if (!next) {
      props.close();
    }
  }

  function handleClose() {
    handleOpenChange(false);
  }

  const handleCheckout = async () => {
    if (!isPayingCustomer || !priceObject) {
      return;
    }
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      priceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  return (
    <Modal
      title="Buy more Spins"
      maxWidth={684}
      open={open}
      onOpenChange={handleOpenChange}
    >
      {isPayingCustomer ? (
        <Text as="p" variant="studio/body/md">
          Spins are part of our legacy billing system. You can still purchase
          extra Spins here if you&apos;re on a legacy plan. Our new Individual
          and Team plans use monthly credits and include more value —{' '}
          <a
            href={getHomepageUrl('/pricing')}
            className="text-blue underline"
          >
            compare plans
          </a>
          .
        </Text>
      ) : (
        <>
          <Text as="p" variant="studio/body/md">
            Spins are a legacy credit system on older Cartwheel plans. We&apos;ve
            moved to monthly credits on our new Individual and Team plans, which
            offer more usage, workspace features, and better value.
          </Text>
          <Text
            as="p"
            variant="studio/body/md"
            className="text-gray-60 dark:text-gray-45 mt-4"
          >
            To get more out of Cartwheel, upgrade to a current plan instead of
            buying legacy Spins.
          </Text>
        </>
      )}
      {isPayingCustomer && (
        <>
          <div className="bg-gray-15 dark:bg-gray-75 mt-6 flex items-center justify-between rounded-lg py-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 pl-4">
                <Button
                  className="dark:bg-gray-90 h-8 w-8 bg-white"
                  variant="secondary"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  <MinusIcon className="h-4 w-4" />
                </Button>
                <div className="flex min-w-48 items-center justify-center">
                  <Text as="span" variant="studio/display/2xl">
                    {quantity * productStore.SPINS_PER_PURCHASE}
                  </Text>
                </div>
                <Button
                  className="dark:bg-gray-90 h-8 w-8 bg-white"
                  variant="secondary"
                  size="sm"
                  disabled={quantity >= 25}
                  onClick={() => setQuantity(quantity + 1)}
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
                <div className="pt-8">
                  <Text
                    as="span"
                    variant="studio/body/bold/md"
                    className="font-display"
                  >
                    Spins
                  </Text>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 pr-4">
              <Text as="span" variant="studio/body/md">
                Total:
              </Text>
              <Text as="span" variant="studio/body/bold/md">
                ${productStore.formatMoney(quantity * spinPrice)}.00
              </Text>
            </div>
          </div>
        </>
      )}
      <div className="mt-6 flex flex-row flex-nowrap">
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        {isPayingCustomer ? (
          <Button
            className="ml-auto"
            variant="primary"
            onClick={handleCheckout}
            disabled={!priceObject}
          >
            Checkout
          </Button>
        ) : (
          <Button
            className="ml-auto"
            variant="primary"
            onClick={() => {
              window.location.href = getHomepageUrl('/pricing');
            }}
          >
            View plans
          </Button>
        )}
      </div>
    </Modal>
  );
}
